import { useEffect, useState, type ReactNode } from "react";
import { modelLabel } from "../../shared/models";
import type { AgentDTO, ScheduleDTO, ScheduleModelsDTO, ScheduleTrigger, SubjectDTO } from "../../shared/types";
import { useUser } from "../App";
import { del, get, patch, post } from "../lib/api";
import { cronPreview } from "../lib/cron-words";
import { draftFrom, destinationsInput, othersOf, sameDestinations } from "../lib/schedule-destinations";
import { handoverActions, mayChooseRunsAs, runsAsWords, scheduleActions } from "../lib/schedules";
import { useScopeTypes } from "../lib/scopes";
import { AgentAvatar } from "./AgentAvatar";
import { confirmDialog, Dialog } from "./Dialog";
import { Icon } from "./Icon";
import { List, ListRow } from "./List";
import { ModelBadge } from "./ModelBadge";
import { DestinationsField, DestinationsLine, RunNowDialog } from "./ScheduleDestinations";
import { HandoverDialog, OfferLine } from "./ScheduleHandover";
import { ScopePicker } from "./ScopePicker";
import { Switch } from "./Switch";
import { Avatar, Badge, Button, Choice, ErrorText, Field, FieldGroup, inputCls, StatusBadge, SubjectBadge, timeAgo } from "./ui";

/** When a schedule fires, in words: "0 9 * * 1-5 (Europe/Athens)", "once at 23 Oct, 11:00", "by hand". */
export function whenLine(s: Pick<ScheduleDTO, "trigger" | "cron" | "timezone" | "runAt">): string {
  if (s.trigger === "cron") return `${s.cron}${s.timezone ? ` (${s.timezone})` : ""}`;
  if (s.trigger === "once") return s.runAt ? `once at ${new Date(s.runAt).toLocaleString()}` : "once";
  return "by hand";
}

const TRIGGER_ICON = { cron: "clock", once: "bell", manual: "play" } as const;

/** Who a schedule runs as, as a tag on its row (AIT-59): the agent's picture or the person's, and "Runs as <name>". */
export function RunsAsTag({ schedule }: { schedule: ScheduleDTO }) {
  const agent = schedule.runsAs === "agent";
  return (
    <span title={agent ? `It runs on its own, with ${schedule.agentName ?? "the agent"}'s workspace keys` : `It runs as ${schedule.ownerName ?? "its person"}, with their own logins`}>
      <Badge>
        <span className="inline-flex items-center gap-1">
          {agent ? <AgentAvatar agent={schedule.agentId} name={schedule.agentName ?? "Agent"} size="xs" /> : <Avatar name={schedule.ownerName ?? "?"} size="xs" />}
          {runsAsWords(schedule)}
        </span>
      </Badge>
    </span>
  );
}

/**
 * Schedules as a list: what fires when, whose it is, how it last went, and only the actions the platform would allow
 * the person looking (D5, D6): its owner runs, changes, pauses and deletes it; an owner of its agent pauses and deletes it;
 * one that runs as the agent itself is its agent's owners' in full (AIT-56). Edit opens the schedule's dialog;
 * `scopeAgent` (an agent's own page) adds its About field and its owners' "Runs as" choice there. `runsAsTag`: each row
 * says who it runs as, beside where its answer goes (AIT-59).
 */
export function ScheduleList({ schedules, title, hint, empty, action, footer, ownsAgent, showAgent = false, runsAsTag = false, scopeAgent, onChanged }: {
  schedules: ScheduleDTO[]; title: string; hint?: string; empty: ReactNode; action?: ReactNode; footer?: ReactNode;
  /** Whether the person looking owns the agent each schedule is on. */
  ownsAgent: (agentId: string) => boolean;
  showAgent?: boolean;
  runsAsTag?: boolean;
  scopeAgent?: AgentDTO;
  onChanged: () => void;
}) {
  const me = useUser();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const edited = schedules.find((s) => s.id === editing) ?? null;
  // "Run now" on one that also reaches other people or a channel asks first (AIT-55).
  const [running, setRunning] = useState<string | null>(null);
  const toRun = schedules.find((s) => s.id === running) ?? null;
  // "Hand over to…" (AIT-62): the schedule being offered to someone.
  const [handing, setHanding] = useState<string | null>(null);
  const toHand = schedules.find((s) => s.id === handing) ?? null;
  const run = (s: ScheduleDTO, onlyToMe = false) => act(() => post(`/api/schedules/${s.id}/run`, onlyToMe ? { onlyToMe } : {}));
  const [error, setError] = useState<string | null>(null);
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <List title={title} hint={hint} action={action} empty={empty} footer={(error || footer) && <>{footer}<ErrorText error={error} /></>}>
        {schedules.map((s) => {
          const mine = s.ownerUserId === me.id;
          const own = s.runsAs === "agent";
          // While it is offered (AIT-62) it does not run, so Run now, Pause and Resume wait for the answer.
          const can = { ...scheduleActions(s, me.id, ownsAgent(s.agentId)), ...(s.offer ? { canRun: false, canPause: false, canResume: false } : {}) };
          const hand = handoverActions(s, me, ownsAgent(s.agentId));
          // A once schedule whose time has passed switches on only with a new time, which is the owner's to give.
          const spent = s.trigger === "once" && !s.enabled && (!s.runAt || Date.parse(s.runAt) <= Date.now());
          return (
            <ListRow
              key={s.id}
              leading={<Icon name={TRIGGER_ICON[s.trigger]} className="size-4 text-muted" />}
              title={<span className="truncate text-sm font-medium">{s.name}</span>}
              subtitle={
                <>
                  {whenLine(s)}
                  {s.model && <> <ModelBadge model={s.model} short title="Picked for this schedule" /></>}
                  {showAgent && s.agentName ? ` · ${s.agentName}` : ""}
                  {own ? ` · set up by ${s.setUpByName ?? "someone who has left"}` : !mine && !runsAsTag && s.ownerName ? ` · ${s.ownerName}'s` : ""}
                  {s.enabled && s.nextRunAt ? ` · next ${new Date(s.nextRunAt).toLocaleString()}` : ""}
                  {s.lastRunAt ? ` · last ${timeAgo(s.lastRunAt)}` : ""}
                  {s.pausedReason && !s.offer ? ` · paused: ${s.pausedReason}` : ""}
                </>
              }
              trailing={
                <>
                  <SubjectBadge subjectKey={s.subjectKey} subjectName={s.subjectName} />
                  {s.lastRunStatus && <StatusBadge status={s.lastRunStatus} />}
                  {s.enabled ? <Badge tone="emerald">on</Badge> : <Badge>off</Badge>}
                  {can.canRun && (
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => (othersOf(s).length ? setRunning(s.id) : run(s))}>
                      Run now
                    </Button>
                  )}
                  {can.canEdit && (
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => setEditing(s.id)}>
                      Edit
                    </Button>
                  )}
                  {hand.canOffer && (
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => setHanding(s.id)}>
                      Hand over
                    </Button>
                  )}
                  {(s.enabled ? can.canPause : can.canResume) && !spent && (
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => act(() => patch(`/api/schedules/${s.id}`, { enabled: !s.enabled }))}>
                      {s.enabled ? "Pause" : "Resume"}
                    </Button>
                  )}
                  {can.canDelete && (
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={busy}
                      onClick={async () =>
                        (await confirmDialog({ title: `Delete the schedule "${s.name}"?`, body: mine ? "It stops for good." : own ? `It is ${s.agentName ?? "the agent"}'s own; it stops for good, and nobody it goes to hears from it again.` : `It is ${s.ownerName ?? "its owner"}'s; it stops for good, and they see it gone.`, confirm: "Delete schedule", danger: true })) &&
                        act(() => del(`/api/schedules/${s.id}`))
                      }
                    >
                      Delete
                    </Button>
                  )}
                </>
              }
            >
              <p className="mt-1 line-clamp-2 text-sm whitespace-pre-wrap text-muted">{s.prompt}</p>
              {runsAsTag ? (
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <RunsAsTag schedule={s} />
                  <DestinationsLine schedule={s} className="" />
                </div>
              ) : (
                <DestinationsLine schedule={s} />
              )}
              <OfferLine schedule={s} canWithdraw={hand.canWithdraw} busy={busy} onWithdraw={() => act(() => post(`/api/schedules/${s.id}/offer/withdraw`, {}))} />
            </ListRow>
          );
        })}
      </List>
      {edited && (
        <ScheduleDialog
          schedule={edited}
          agentId={edited.agentId}
          agentName={edited.agentName ?? "the agent"}
          scopeAgent={scopeAgent?.id === edited.agentId ? scopeAgent : undefined}
          onHandOver={handoverActions(edited, me, ownsAgent(edited.agentId)).canOffer ? () => { setEditing(null); setHanding(edited.id); } : undefined}
          onSaved={onChanged}
          onClose={() => setEditing(null)}
        />
      )}
      {toHand && <HandoverDialog schedule={toHand} onDone={onChanged} onClose={() => setHanding(null)} />}
      {toRun && (
        <RunNowDialog
          schedule={toRun}
          busy={busy}
          onRun={(onlyToMe) => void run(toRun, onlyToMe).then(() => setRunning(null))}
          onClose={() => setRunning(null)}
        />
      )}
    </>
  );
}


const TRIGGER_CHOICES: { value: ScheduleTrigger; label: string }[] = [
  { value: "once", label: "Once" }, { value: "cron", label: "Repeats" }, { value: "manual", label: "By hand" },
];
const NAME_MAX = 120;
const PROMPT_MAX = 8_000;
const ONCE_MAX_DAYS = 366;

/** A time as a datetime-local field writes it, in the browser's own clock: "2026-10-26T09:00". */
const localTime = (at: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
};

/** What a cron schedule will do, under its field: in words when it has a common shape, and its next three times. */
function CronPreview({ cron, timezone }: { cron: string; timezone: string }) {
  if (!cron.trim()) return null;
  const preview = cronPreview(cron, timezone || null);
  if ("error" in preview) return <p className="text-xs text-danger">Not a schedule the platform can run: {preview.error}</p>;
  return (
    <p className="text-xs text-muted">
      {preview.words && <span className="font-medium text-fg">{preview.words} · </span>}
      next {preview.next.join(" · ")} <span className="text-faint">({preview.zone})</span>
    </p>
  );
}

/** "About": the scope a schedule's run works under, from the scope types the agent works with; nothing when it has none. */
function ScopeAbout({ agent, value, onChange }: { agent: AgentDTO; value: SubjectDTO | null; onChange: (scope: SubjectDTO | null) => void }) {
  const { types } = useScopeTypes();
  const own = (types ?? []).filter((t) => agent.scopeTypes.includes(t.key));
  if (own.length === 0) return null;
  return (
    <div>
      <div className="mb-1.5 text-xs font-medium text-muted">About</div>
      <ScopePicker types={own} value={value} onChange={onChange} placeholder="Nothing in particular: search a scope" />
      <p className="mt-1.5 text-xs text-muted">Optional. {agent.name} then works on it under that scope.</p>
    </div>
  );
}

/**
 * A schedule in the platform's dialog (D6, D12): new on an agent's Schedules tab, or one edited from any list. One
 * agent, one prompt, one trigger. Its first field is whom it runs as (AIT-59): the person looking, answering in their
 * own chat with the agent, or the agent itself, which its owners set up and change (AIT-56) and which answers only
 * where they send it, so somewhere is required. Only the agent's owners choose, on its page, and on an edit only for
 * one that is theirs or the agent's; everyone else reads it. Right after it, the model (AIT-76): the agent's default, or
 * what that Runs as may pick, asked of the server each time it changes; a pick it does not offer goes back to the default,
 * and the field says so. Its on/off sits in the header: part of the form on a new one, saved on the spot on an edit (the
 * kit's rule for an item's own switch). An edit sends only what changed, a trigger always with its own fields, and a
 * switch of whom it runs as with where it goes.
 */
export function ScheduleDialog({ schedule, agentId, agentName, scopeAgent, onHandOver, onSaved, onClose }: {
  /** The schedule being edited; null for a new one. */
  schedule: ScheduleDTO | null;
  agentId: string;
  agentName: string;
  /** The agent, when its own page opened the dialog: its scope types give the About field, and its owners choose "Runs as". */
  scopeAgent?: AgentDTO;
  /** "Hand over to…" (AIT-62), for whoever may offer this schedule to someone. */
  onHandOver?: () => void;
  onSaved: () => void;
  onClose: () => void;
}) {
  const me = useUser();
  // A new one runs as the person looking until an owner of the agent chooses the agent itself.
  const initialRunsAs = schedule?.runsAs === "agent" ? "agent" : "me";
  const [runsAs, setRunsAs] = useState<"me" | "agent">(initialRunsAs);
  const choose = scopeAgent ? mayChooseRunsAs(scopeAgent, schedule, me.id) : false;
  const own = runsAs === "agent";
  const switching = schedule !== null && runsAs !== initialRunsAs;
  const initialAt = schedule?.runAt ? localTime(new Date(schedule.runAt)) : "";
  const [name, setName] = useState(schedule?.name ?? "");
  const [trigger, setTrigger] = useState<ScheduleTrigger>(schedule?.trigger ?? "once");
  const [cron, setCron] = useState(schedule?.cron ?? "0 9 * * 1-5");
  // A new schedule takes the browser's timezone; an edit keeps what it has, none included (the server's, UTC).
  const [timezone, setTimezone] = useState(schedule ? (schedule.timezone ?? "") : Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Athens");
  const [runAt, setRunAt] = useState(initialAt);
  const [prompt, setPrompt] = useState(schedule?.prompt ?? "");
  const [initialDraft] = useState(() => draftFrom(schedule?.destinations ?? []));
  const [draft, setDraft] = useState(initialDraft);
  const [enabled, setEnabled] = useState(schedule?.enabled ?? true);
  const [picked, setPicked] = useState(schedule?.model ?? "");
  const [models, setModels] = useState<ScheduleModelsDTO | null>(null);
  const [about, setAbout] = useState<SubjectDTO | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (schedule?.subjectId) get<SubjectDTO>(`/api/subjects/${schedule.subjectId}`).then(setAbout, () => setAbout(null));
  }, [schedule?.subjectId]);

  useEffect(() => {
    let current = true;
    setModels(null);
    get<ScheduleModelsDTO>(`/api/agents/${agentId}/schedule-models?runsAs=${runsAs}`).then((m) => current && setModels(m), (err) => current && setError(err.message));
    return () => {
      current = false;
    };
  }, [agentId, runsAs]);
  const pickOffered = !picked || !models || models.options.some((m) => m.id === picked);
  // Its saved pick stays until someone changes it, even one gone for now: only a new Runs as, or a new pick, falls back.
  const savedPick = !pickOffered && picked === (schedule?.model ?? "") && runsAs === initialRunsAs;
  const model = pickOffered || savedPick ? picked : "";

  // A once schedule whose time has passed is off for good until it is given a new time.
  const spent = schedule !== null && schedule.trigger === "once" && (!schedule.runAt || Date.parse(schedule.runAt) <= Date.now());
  const now = new Date();
  const ready = name.trim() && prompt.trim() && (trigger !== "cron" || cron.trim()) && (trigger !== "once" || runAt) && (!own || destinationsInput(draft).length > 0);

  // An admin's schedule names people and channels, as does the agent's own; a member's only their own Slack DM, as old callers say it.
  // Run as you, your own web chat is its fixed line, so it is never one of the places named.
  const sent = own ? draft : { ...draft, webs: draft.webs.filter((id) => id !== me.id) };
  const where = () => (own || me.role === "admin" ? { destinations: destinationsInput(sent) } : { slackDm: draft.dms.includes(me.id) });

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const group =
        trigger === "cron" ? { trigger, cron: cron.trim(), timezone: timezone.trim() || null }
        : trigger === "once" ? { trigger, runAt: new Date(runAt).toISOString() }
        : { trigger };
      if (!schedule) {
        await post("/api/schedules", { name: name.trim(), agentId, prompt: prompt.trim(), ...where(), enabled, ...group, ...(about ? { subjectId: about.id } : {}), ...(own ? { runsAs: "agent" } : {}), ...(model ? { model } : {}) });
      } else {
        const changed =
          trigger !== schedule.trigger ||
          (trigger === "cron" && (cron.trim() !== schedule.cron || (timezone.trim() || null) !== schedule.timezone)) ||
          (trigger === "once" && runAt !== initialAt);
        const body: Record<string, unknown> = {
          ...(name.trim() !== schedule.name ? { name: name.trim() } : {}),
          ...(prompt.trim() !== schedule.prompt ? { prompt: prompt.trim() } : {}),
          ...(switching ? { runsAs, ...where() } : !sameDestinations(draft, initialDraft) ? where() : {}),
          ...(changed ? group : {}),
          ...(model !== (schedule.model ?? "") ? { model: model || null } : {}),
          // A new time is what switches a spent once schedule back on.
          ...(changed && trigger === "once" && spent ? { enabled: true } : {}),
          ...(scopeAgent && (about?.id ?? null) !== schedule.subjectId ? { subjectId: about?.id ?? null } : {}),
        };
        if (Object.keys(body).length) await patch(`/api/schedules/${schedule.id}`, body);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (on: boolean) => {
    if (!schedule) return setEnabled(on);
    setBusy(true);
    setError(null);
    try {
      await patch(`/api/schedules/${schedule.id}`, { enabled: on });
      setEnabled(on);
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      onClose={onClose}
      size="lg"
      title={schedule ? `Edit ${schedule.name}` : "New schedule"}
      description={
        own
          ? `It runs on its own, with ${agentName}'s workspace keys, not your logins. Its answer goes only where you send it below.`
          : `Yours: it runs as you, and ${agentName}'s answer lands in your own chat with it, and wherever else you send it below.`
      }
      headerAction={
        <span title={spent && !enabled ? "Its time has passed: give it a new one, and saving switches it back on" : undefined}>
          <Switch aria-label={enabled ? "On" : "Off"} checked={enabled} disabled={busy || (spent && !enabled)} onChange={toggle} />
        </span>
      }
      busy={busy}
      secondary={
        onHandOver && (
          <Button variant="ghost" disabled={busy} onClick={onHandOver}>
            Hand over to…
          </Button>
        )
      }
      primary={
        <Button variant="primary" disabled={busy || !ready} onClick={save}>
          {schedule ? (busy ? "Saving…" : "Save") : busy ? "Creating…" : "Create schedule"}
        </Button>
      }
    >
      <div className="space-y-4">
        {schedule?.offer && (
          <p className="rounded-xl bg-warning/10 px-3 py-2 text-sm text-warning">
            Waiting for {schedule.offer.toName} to accept it. Saving a change withdraws the offer, and it is back as it was.
          </p>
        )}
        {!enabled && !schedule?.offer && (
          <p className="rounded-xl bg-warning/10 px-3 py-2 text-sm text-warning">
            {spent ? "Off: its time has passed. Give it a new time and save to switch it back on." : "Off: it does not fire until you switch it on. Run now still works."}
          </p>
        )}
        <FieldGroup
          label="Runs as"
          hint={
            choose && (
              <>
                <span className="block">Me: runs with your own logins and answers in your chat.</span>
                <span className="block">{agentName} itself: runs on its own with the agent's workspace keys, so choose below where its answer goes.</span>
              </>
            )
          }
        >
          {choose ? (
            <Choice
              label="Runs as"
              value={runsAs}
              onChange={setRunsAs}
              options={[
                { value: "me", label: <><Avatar name={me.name} size="xs" /> Me ({me.name})</> },
                { value: "agent", label: <><AgentAvatar agent={agentId} name={agentName} size="xs" /> {agentName} itself</> },
              ]}
            />
          ) : (
            <p className="flex min-w-0 items-center gap-2 text-sm">
              {own ? <AgentAvatar agent={agentId} name={agentName} size="sm" /> : <Avatar name={schedule && schedule.ownerUserId !== me.id ? (schedule.ownerName ?? "?") : me.name} size="sm" />}
              <span className="truncate">{own ? `Runs as ${agentName} itself` : schedule && schedule.ownerUserId !== me.id ? `Runs as ${schedule.ownerName ?? "someone who has left"}` : "Runs as you"}</span>
            </p>
          )}
        </FieldGroup>
        <Field
          label="Model"
          hint={
            savedPick
              ? `${modelLabel(picked)} is no longer available, so this schedule pauses until it is back or you pick another.`
              : !pickOffered
                ? `${modelLabel(picked)} is not available here, so it runs on the agent's default${models?.why ? `: ${models.why}` : ""}.`
                : (models?.why ?? (own ? "Only what its model list can pay for." : "Pick a lighter model for simple work. It runs in a session of its own, and your chat stays on its model."))
          }
        >
          <select className={inputCls} value={model} onChange={(e) => setPicked(e.target.value)}>
            <option value="">{models ? `Agent's default (${modelLabel(models.defaultModel)})` : "Agent's default"}</option>
            {savedPick && <option value={picked}>{modelLabel(picked)} (no longer available)</option>}
            {models?.options.filter((m) => m.id !== models.defaultModel || m.id === schedule?.model).map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </Field>
        <dl className="grid grid-cols-[4.5rem_1fr] items-center gap-y-2 text-sm">
          {own && schedule?.runsAs === "agent" && (
            <>
              <dt className="text-xs font-medium text-muted">Set up by</dt>
              <dd className="flex min-w-0 items-center gap-2">
                <span className="truncate">{schedule.setUpBy === me.id ? "you" : (schedule.setUpByName ?? "someone who has left")}</span>
              </dd>
            </>
          )}
          <dt className="text-xs font-medium text-muted">Agent</dt>
          <dd className="flex min-w-0 items-center gap-2">
            <AgentAvatar agent={agentId} name={agentName} size="sm" /> <span className="truncate">{agentName}</span>
          </dd>
        </dl>
        <Field label="Name">
          <input className={inputCls} value={name} maxLength={NAME_MAX} onChange={(e) => setName(e.target.value)} placeholder="Daily report" />
        </Field>
        <Field label="What to do" hint={own ? `Given to ${agentName} each time it fires. Write it to stand on its own: nobody is there to answer a question.` : `Given to ${agentName} each time it fires, as your request. Write it to stand on its own.`}>
          <textarea className={inputCls} rows={5} value={prompt} maxLength={PROMPT_MAX} onChange={(e) => setPrompt(e.target.value)} />
        </Field>
        <Choice label="When" value={trigger} options={TRIGGER_CHOICES} onChange={setTrigger} />
        {trigger === "once" && (
          <Field label="At" hint="Your own clock. It runs once, then switches itself off.">
            <input
              className={inputCls}
              type="datetime-local"
              value={runAt}
              min={localTime(now)}
              max={localTime(new Date(now.getTime() + ONCE_MAX_DAYS * 86_400_000))}
              onChange={(e) => setRunAt(e.target.value)}
            />
          </Field>
        )}
        {trigger === "cron" && (
          <div className="space-y-2">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_12rem]">
              <Field label="Every… (cron)" hint="Minute hour day month weekday: 0 9 * * 1-5 is weekdays at 09:00.">
                <input className={`${inputCls} font-mono`} value={cron} onChange={(e) => setCron(e.target.value)} />
              </Field>
              <Field label="Timezone">
                <input className={inputCls} value={timezone} placeholder="UTC" onChange={(e) => setTimezone(e.target.value)} />
              </Field>
            </div>
            <CronPreview cron={cron} timezone={timezone} />
          </div>
        )}
        {trigger === "manual" && <p className="text-xs text-muted">{own ? "It runs only when one of its owners presses Run now." : "It runs only when you press Run now."}</p>}
        {own && trigger === "cron" && <p className="text-xs text-muted">At most once an hour.</p>}
        {scopeAgent && <ScopeAbout agent={scopeAgent} value={about} onChange={setAbout} />}
        <DestinationsField draft={draft} saved={schedule?.destinations ?? []} ownChat={!own} onChange={setDraft} />
        <ErrorText error={error} />
      </div>
    </Dialog>
  );
}
